"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { Languages } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const localeLabels = {
  en: "English",
  es: "Español",
  it: "Italiano"
};

export default function LanguageSwitcher({ className = "", isMobile = false }) {
  const locale = useLocale();
  const t = useTranslations("common.languageSwitcher");
  const router = useRouter();
  const pathname = usePathname();
  const { updatePreferredLocale } = useAuth();

  const [isOpen, setIsOpen] = useState(false);

  const locales = ["en", "es", "it"];

  function handleSelect(nextLocale) {
    setIsOpen(false);

    if (nextLocale === locale) {
      return;
    }

    updatePreferredLocale(nextLocale);

    const segments = pathname.split("/").filter(Boolean);

    if (segments.length > 0 && locales.includes(segments[0])) {
      segments[0] = nextLocale;
    } else {
      segments.unshift(nextLocale);
    }

    const newPath = `/${segments.join("/")}` || "/";
    router.push(newPath === "//" ? "/" : newPath);
  }

  const triggerClasses = isMobile
    ? `flex w-full items-center justify-center px-4 py-2 text-sm text-[#FBFBFE] hover:text-[#D4B6FF] ${className}`
    : `flex items-center justify-center rounded-lg border border-[#6A3683] px-3 py-2 text-sm text-[#FBFBFE] transition hover:border-[#A45DE8] hover:bg-[#6A3683]/20 ${className}`;

  const dropdownClasses = `absolute right-0 mt-2 min-w-[160px] rounded-lg border border-[#6A3683]/40 bg-[#050316] p-1 shadow-lg`;

  return (
    <div className="relative">
      <button
        type="button"
        className={triggerClasses}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${t("ariaLabel")} (${localeLabels[locale]})`}
      >
        <Languages className="h-4 w-4" aria-hidden="true" />
        <span className="sr-only">{localeLabels[locale] ?? locale.toUpperCase()}</span>
      </button>

      {isOpen && (
        <ul
          className={dropdownClasses}
          role="listbox"
          aria-label={t("ariaLabel")}
        >
          {locales.map((entry) => (
            <li key={entry}>
              <button
                type="button"
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition hover:bg-[#6A3683]/30 ${
                  entry === locale ? "text-[#D4B6FF]" : "text-[#FBFBFE]"
                }`}
                onClick={() => handleSelect(entry)}
                role="option"
                aria-selected={entry === locale}
              >
                <span>{localeLabels[entry]}</span>
                {entry === locale && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

